import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useBlocker } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'

// 「アクティブな録音セッションが画面に存在する」 を 1 boolean で表す global Context.
// 既存 useRecordingEngine (= per-MeetingBlock の local lifecycle hook) とは semantic を
// 完全に分離しており、 こちらは Sidebar / IconBar / breadcrumb 等の **画面遷移を抑止する**
// 受け手側だけが見る. recording を持っている emitter (= MeetingBlock) は state ∈
// {recording, paused, stopping, failed} のあいだ setActiveRecording(true) を立てる.
//
// 抑止 layer は 3 段:
//   1. consumer の disabled / aria-disabled (= Sidebar / IconBar / ContextPane で実装)
//   2. beforeunload (= browser close / hard reload を捕捉)
//   3. useBlocker (= Router 内 navigate を中央捕捉. Data Router 必須.
//      createBrowserRouter + RouterProvider 構成で有効化)

interface ActiveRecordingContextValue {
  isActiveRecording: boolean
  setActiveRecording: (next: boolean) => void
  /**
   * 「録音中の他画面遷移」 抑止 dialog で `[中断して移動]` を押された時に呼び出される.
   * MeetingBlock が録音 instance の cancel() を mount 中 register する.
   * register されていなければ dialog は cancel handler を呼ばずに proceed する.
   */
  registerCancelHandler: (handler: () => void) => void
  unregisterCancelHandler: () => void
}

const ActiveRecordingContext = createContext<ActiveRecordingContextValue>({
  isActiveRecording: false,
  setActiveRecording: () => {},
  registerCancelHandler: () => {},
  unregisterCancelHandler: () => {},
})

export const useActiveRecording = (): ActiveRecordingContextValue =>
  useContext(ActiveRecordingContext)

export const RECORDING_NAV_BLOCK_HINT = '録音中は他の画面に移動できません'

interface ProviderProps {
  children: ReactNode
}

export const ActiveRecordingProvider = ({ children }: ProviderProps) => {
  const [isActiveRecording, setIsActiveRecording] = useState(false)
  // useBlocker のチェック関数 closure で isActiveRecording を読むと、 同一 React commit 内で
  // setActiveRecording(false) + navigate() が走った場合に flush 前の古い値を見る race が出る
  // (= 録音完了 commit で MeetingBlock effect が false に倒した直後、 同 commit 内で
  // NewRecordingPage effect が navigate を呼ぶ flow). ref で同期更新して構造的に race を潰す.
  const isActiveRecordingRef = useRef(false)
  const cancelHandlerRef = useRef<(() => void) | null>(null)

  const setActiveRecording = useCallback((next: boolean) => {
    isActiveRecordingRef.current = next
    setIsActiveRecording(next)
  }, [])

  const registerCancelHandler = useCallback((handler: () => void) => {
    cancelHandlerRef.current = handler
  }, [])
  const unregisterCancelHandler = useCallback(() => {
    cancelHandlerRef.current = null
  }, [])

  useEffect(() => {
    if (!isActiveRecording) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isActiveRecording])

  // Router 内 navigate を録音中に抑止. 同一 URL navigation では発火しない (= invalidateQueries
  // 等の同 path 更新は素通り).
  // チェック関数は ref を読む (= state ではない) ことで race を回避.
  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string }
      nextLocation: { pathname: string }
    }) =>
      isActiveRecordingRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
    []
  )
  const blocker = useBlocker(shouldBlock)

  const onCancelAndProceed = useCallback(() => {
    const cancel = cancelHandlerRef.current
    if (cancel) {
      try {
        cancel()
      } catch {
        // 失敗しても routing は進める (= cancel は best-effort).
      }
    }
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }, [blocker])

  const onCancelDialog = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  return (
    <ActiveRecordingContext.Provider
      value={{
        isActiveRecording,
        setActiveRecording,
        registerCancelHandler,
        unregisterCancelHandler,
      }}
    >
      {children}
      <Dialog
        open={blocker.state === 'blocked'}
        onClose={onCancelDialog}
        aria-labelledby="active-recording-leave-confirm-title"
      >
        <DialogTitle id="active-recording-leave-confirm-title">
          録音中です
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            画面を移動すると現在の録音は破棄されます。 中断して移動しますか?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelDialog} color="inherit">
            キャンセル
          </Button>
          <Button onClick={onCancelAndProceed} color="error" autoFocus>
            中断して移動
          </Button>
        </DialogActions>
      </Dialog>
    </ActiveRecordingContext.Provider>
  )
}
